import React from 'react';
import { Card, Row, Col, Input, Select, InputNumber, Button, Space, Tag } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { FilterValues } from '../../components/TableFilter';
import styles from '../../pages/admin/Subscribers.module.css';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

interface PaymentFiltersProps {
  filters: FilterValues;
  numberRangeFilters: { amount?: [number | undefined, number | undefined] };
  setFilters: (filters: FilterValues) => void;
  setNumberRangeFilters: (filters: any) => void;
  onClearAll: () => void;
}

const paymentStatuses = [
  'COMPLETED',
  'PENDING',
  'ABORTED',
  'CANCELED',
  'INITIALED',
  'REQUESTED',
  'COMPLETED_WITH_ERROR',
  'COMPLETED_AND_CONSUMED',
  'FAILED',
  'REFUNDED',
  'REFUND_REQUESTED',
  'REFUND_COMPLETED',
  'REFUND_FAILED',
];

export const PaymentFilters: React.FC<PaymentFiltersProps> = ({
  filters,
  numberRangeFilters,
  setFilters,
  setNumberRangeFilters,
  onClearAll,
}) => {
  const { t } = useTranslation();

  const getActiveFilterCount = () => {
    const textSelectCount = Object.values(filters).filter(v => v !== undefined && v !== '').length;
    const numberCount = Object.values(numberRangeFilters).filter(([min, max]) => min !== undefined || max !== undefined).length;
    return textSelectCount + numberCount;
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <FilterOutlined /> {t('filters.title')}
          {getActiveFilterCount() > 0 && <Tag color="blue">{getActiveFilterCount()}</Tag>}
        </Space>
      }
    >
      <Row gutter={[16, 16]}>

        {/* Description */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('common.description')}</label>
          <Input
            placeholder={t('common.description')}
            value={filters.description || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, description: e.target.value }))}
            className={styles.customInput}
          />
        </Col>

        {/* Agence */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('common.agency')}</label>
          <Input
            placeholder={t('common.agency')}
            value={filters.agencyName || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, agencyName: e.target.value }))}
            className={styles.customInput}
          />
        </Col>

        {/* Date */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('common.date')}</label>
          <DatePicker.RangePicker
            className={styles.customDateRange}
            style={{ width: '100%' }}
            value={[
              filters.dateMin ? moment(filters.dateMin) : null,
              filters.dateMax ? moment(filters.dateMax) : null
            ]}
            onChange={(dates) => {
              setFilters(prev => ({
                ...prev,
                dateMin: dates?.[0]?.startOf('day').toISOString(),
                dateMax: dates?.[1]?.endOf('day').toISOString(),
              }));
            }}
            allowClear
          />
        </Col>

        {/* Montant */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('filters.amountEuro')}</label>
          <Input.Group compact>
            <InputNumber
              style={{ width: '50%' }}
              placeholder="Min"
              value={numberRangeFilters.amount?.[0]}
              onChange={(val) => setNumberRangeFilters((prev: any) => ({ ...prev, amount: [val, prev.amount?.[1]] }))}
              className={styles.customInputNumber}
            />
            <InputNumber
              style={{ width: '50%' }}
              placeholder="Max"
              value={numberRangeFilters.amount?.[1]}
              onChange={(val) => setNumberRangeFilters((prev: any) => ({ ...prev, amount: [prev.amount?.[0], val] }))}
              className={styles.customInputNumber}
            />
          </Input.Group>
        </Col>

        {/* Méthode */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('common.method')}</label>
          <Select
            placeholder={t('filters.selectPlaceholder')}
            value={filters.method || undefined}
            onChange={(val) => setFilters(prev => ({ ...prev, method: val }))}
            allowClear
            style={{ width: '100%' }}
            className={styles.customSelect}
          >
            <Option value="CARD">CARD</Option>
            <Option value="TRANSFER">TRANSFER</Option>
            <Option value="CASH">CASH</Option>
          </Select>
        </Col>

        {/* Statut */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('common.status')}</label>
          <Select
            placeholder={t('filters.selectPlaceholder')}
            value={filters.status || undefined}
            onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
            allowClear
            style={{ width: '100%' }}
            className={styles.customSelect}
          >
            {paymentStatuses.map((status) => (
                <Option key={status} value={status}>
                    {status}
                </Option>
            ))}
          </Select>
        </Col>

        {/* Bouton Réinitialiser */}
        <Col xs={24} sm={24} md={8}>
          <div style={{ paddingTop: 15, paddingLeft: 1350 }}>
            <Space>
              <Button icon={<ClearOutlined />} onClick={onClearAll} disabled={getActiveFilterCount() === 0}>
                {t('filters.reset')}
              </Button>
              {getActiveFilterCount() > 0 && <Tag color="blue">{getActiveFilterCount()} {t('filters.activeFilters')}</Tag>}
            </Space>
          </div>
        </Col>
      </Row>
    </Card>
  );
};
